import { useMutation, useSuspenseQuery } from "@apollo/client";
import { Label } from "@radix-ui/react-label";
import { FilePlus, Trash2, Copy, SearchIcon, PencilIcon } from "lucide-react";
import React, { PropsWithChildren, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

import { Text } from "@/DesignSystem/basic/Text/Text";
import { Input } from "@/DesignSystem/shadcn/Input";
import { ReportInput, TemplateType, gql, useFragment } from "@/apis/nannyml";
import { Dialog, DialogContent } from "@/components/Dialog";
import { reportSummaryFragment } from "@/components/Reporting/AddToReport";
import { CopyRequestPopUp, CreateReportPopUp, createReportMutation } from "@/components/Reporting/CreateReport";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/Table";
import { Button } from "@/components/common/Button";
import { RequestStateLayout } from "@/components/dashboard/RequestStateLayout/RequestStateLayout";
import { useSearchList } from "@/hooks/useSearchList";
import { formatISODateTime } from "@/lib/dateUtils";
import { useParamsModelId } from "@/routes/useParamsModelId";

const copyReportMutation = gql(/* GraphQL */ `
  mutation CopyReport($report: CopyReportInput!) {
    copy_report(report: $report) {
      ...ReportSummary
    }
  }
`);

const getReportsQuery = gql(/* GraphQL */ `
  query GetReports($filter: ReportFilterInput!) {
    reports(reportFilter: $filter) {
      ...ReportSummary
    }
  }
`);

const deleteReportMutation = gql(/* GraphQL */ `
  mutation DeleteReport($reportId: Int!) {
    delete_report(reportId: $reportId) {
      ...ReportSummary
    }
  }
`);

type ReportSummary = {
  id: number;
  title: string;
  creationDate: string;
};

export const ManageReports = () => (
  <React.Suspense fallback={<RequestStateLayout isLoading={true} />}>
    <ManageReportsContainer />
  </React.Suspense>
);

const DeleteReportPopUp = ({
  report,
  open,
  onDeleteReport,
  closeDialog,
}: {
  report: ReportSummary;
  open: boolean;
  onDeleteReport: (reportId: number) => void;
  closeDialog: () => void;
}) => {
  return (
    <>
      {report ? (
        <Dialog open={open} onOpenChange={closeDialog}>
          <DialogContent className="max-w-fit max-h-[90%] overflow-auto">
            <Text className="m-3 p-2">Do you really want to permanently delete the report {report.title}?</Text>
            <div className="m-3 gap-3 flex justify-end">
              <Button onClick={closeDialog} cva={{ intent: "secondary", size: "medium" }}>
                Cancel
              </Button>
              <Button onClick={() => onDeleteReport(report.id)} cva={{ intent: "primary", size: "medium" }}>
                Confirm
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      ) : null}
    </>
  );
};

const ManageReportsContainer = ({ children }: PropsWithChildren) => {
  const modelId = useParamsModelId();
  const { data, error } = useSuspenseQuery(getReportsQuery, {
    variables: { filter: { modelId: modelId } },
  });

  const reportList = useFragment(reportSummaryFragment, data.reports);

  const navigate = useNavigate();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [createReportDialogOpen, setCreateReportDialogOpen] = useState(false);
  const [copyReportDialogOpen, setCopyReportDialogOpen] = useState(false);

  const [reports, setReports] = useState([...reportList]);

  const [saveReport, {}] = useMutation(createReportMutation, {
    update(cache, { data }) {
      if (data) {
        const updatedReport = useFragment(reportSummaryFragment, data.create_report);
        cache.modify({
          fields: {
            reports() {
              cache.updateFragment(
                {
                  id: `Report:${updatedReport.id}`,
                  fragment: reportSummaryFragment,
                },
                (element) => {
                  return { ...element, ...updatedReport };
                }
              );
            },
          },
        });
      }
    },
  });

  const [deleteReport, {}] = useMutation(deleteReportMutation, {
    update(cache, { data }) {
      if (data) {
        const deletedReport = useFragment(reportSummaryFragment, data.delete_report);
        cache.evict({ id: `Report:${deletedReport.id}` });
        cache.gc();
      }
    },
  });
  const [copyReport, {}] = useMutation(copyReportMutation, {
    update(cache, { data }) {
      if (data) {
        const copiedReport = useFragment(reportSummaryFragment, data.copy_report);
        cache.modify({
          fields: {
            reports() {
              cache.updateFragment(
                {
                  id: `Report:${copiedReport.id}`,
                  fragment: reportSummaryFragment,
                },
                (element) => {
                  return { ...element, ...copiedReport };
                }
              );
            },
          },
        });
      }
    },
  });
  const [selectedReportDelete, setSelectedReportDelete] = useState<ReportSummary>();
  const [selectedReportCopy, setSelectedReportCopy] = useState<ReportSummary>();

  const { search, setSearch, results } = useSearchList(reports, "title");

  const sendDeleteRequest = (reportId: number) => {
    deleteReport({
      variables: { reportId: reportId },
    });

    setReports(reports.filter((report) => report.id !== reportId));
    setDialogOpen(false);
  };

  const sendCopyRequest = (reportId: number, dateRange: [number, number] | null, reportName?: string) => {
    copyReport({
      variables: {
        report: {
          id: reportId,
          title: reportName,
          metadata: JSON.stringify(dateRange ? { dateRange: dateRange } : {}),
        },
      },
    }).then(({ data }) => {
      setReports([{ ...data?.copy_report } as ReportSummary, ...reports]);
      setCopyReportDialogOpen(false);
    });
  };

  const onClickCopy = (report: ReportSummary) => {
    setSelectedReportCopy(report);
    setCopyReportDialogOpen(true);
  };

  const onClickDelete = (report: ReportSummary) => {
    setSelectedReportDelete(report);
    setDialogOpen(true);
  };

  const createReport = (reportName: string, dateRange: [number, number] | null, template: TemplateType | null) => {
    const title = {
      id: 0,
      label: "Title",
      args: ["text-2xl"],
      key: "ContentText",
      data: reportName,
      textOptions: {
        placeholder: "Report Title",
        className: "text-2xl",
        cols: 100,
        rows: 1,
        iconSize: 20,
      },
    };
    saveReport({
      variables: {
        report: {
          title: title.data,
          content: JSON.stringify({ data: [title], metadata: dateRange ? { dateRange: dateRange } : {} }),
          modelId: modelId,
          template: template,
        } as ReportInput,
      },
    }).then(({ data }) => {
      if (data) {
        const newReport = useFragment(reportSummaryFragment, data.create_report);
        setReports([newReport, ...reports]);
        navigate(`${newReport.id}`, { state: { editMode: true } });
      }
    });
  };

  return (
    <div>
      <div className=" gap-4 m-6">
        <div className="flex my-2 justify-between">
          <Label className="relative w-[300px] text-slate-400 cursor-text">
            <SearchIcon className="absolute left-3 top-3" size={16} />
            <Input
              className="pl-9 border-gray-600"
              placeholder="Search reports by title..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </Label>
          <Button onClick={() => setCreateReportDialogOpen(true)} cva={{ intent: "primary", size: "small" }}>
            <div className="flex justify-center m-2">
              <FilePlus />
            </div>
            <span>Create new report</span>
          </Button>
        </div>
        <div className="border rounded-md border-gray-600">
          <Table className="odd:[&_tr]:bg-oddBg">
            <TableHeader>
              <TableRow>
                <TableHead className="w-1/3">Report title</TableHead>
                <TableHead className="w-1/3">Creation date</TableHead>
                <TableHead className="w-full">Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody className="w-full">
              {results.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={3} className="text-center text-gray-400 italic">
                    No metrics found
                  </TableCell>
                </TableRow>
              ) : (
                results.map((report) => {
                  return (
                    <TableRow key={report.id}>
                      <TableCell>
                        <Link
                          className="underline hover:text-blue-300 "
                          to={{ pathname: `${report.id}` }}
                          state={{ editMode: true }}
                        >
                          {report.title}
                        </Link>
                      </TableCell>
                      <TableCell>{formatISODateTime(new Date(report.creationDate))}</TableCell>
                      <TableCell>
                        <div className="flex">
                          <Button
                            className="mx-2"
                            cva={{ intent: "primary", size: "small" }}
                            title="View report"
                            onClick={() => navigate(`${report.id}`, { state: { editMode: true } })}
                          >
                            <PencilIcon />
                            Edit
                          </Button>

                          <Button
                            className="mx-2"
                            cva={{ intent: "secondary", size: "small" }}
                            title="Copy Report"
                            onClick={() => onClickCopy(report)}
                          >
                            <Copy />
                            Copy
                          </Button>
                          <Button
                            className="mx-2"
                            cva={{ intent: "reject", size: "small" }}
                            title="Delete report"
                            onClick={() => onClickDelete(report)}
                          >
                            <Trash2 />
                            Delete
                          </Button>
                        </div>
                      </TableCell>
                      <DeleteReportPopUp
                        onDeleteReport={sendDeleteRequest}
                        open={dialogOpen}
                        closeDialog={() => setDialogOpen(false)}
                        report={selectedReportDelete!}
                      />
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <CreateReportPopUp
        onDialogOpen={createReportDialogOpen}
        setDialogOpen={setCreateReportDialogOpen}
        createReport={createReport}
      />
      {selectedReportCopy ? (
        <CopyRequestPopUp
          reportName={selectedReportCopy!.title}
          reportId={selectedReportCopy!.id}
          open={copyReportDialogOpen}
          onOpenChange={setCopyReportDialogOpen}
          copyReport={sendCopyRequest}
        />
      ) : null}
    </div>
  );
};
